/**
 * Infinite Moving Cards Component
 * 
 * A component that creates an infinite scrolling carousel of cards with
 * smooth animations and hover effects. Each card can display a quote,
 * name, title, position, company, and an optional icon.
 * 
 * Features:
 * - Infinite scrolling animation
 * - Configurable direction and speed
 * - Pause on hover
 * - Responsive design
 * - Customizable content
 * - Smooth transitions
 */

'use client';

import { cn } from '@/utils/utils';
import React, { useEffect, useState } from 'react';
import { LucideIcon, ChevronRight } from 'lucide-react';

/**
 * Props interface for the InfiniteMovingCards component
 * @interface InfiniteMovingCardsProps
 * @property {Array<Object>} items - Array of card items
 * @property {'left' | 'right'} [direction='left'] - Animation direction
 * @property {'fast' | 'normal' | 'slow'} [speed='fast'] - Animation speed
 * @property {boolean} [pauseOnHover=true] - Whether to pause on hover
 * @property {string} [className] - Additional CSS classes
 */
export const InfiniteMovingCards = ({
	items,
	direction = 'left',
	speed = 'fast',
	pauseOnHover = true,
	showConnectors = false,
	className,
}: {
	items: {
		quote: string;
		name: string;
		title: string;
		position?: string;
		company?: string;
		icon?: LucideIcon;
	}[];
	direction?: 'left' | 'right';
	speed?: 'fast' | 'normal' | 'slow';
	pauseOnHover?: boolean;
	showConnectors?: boolean;
	className?: string;
}) => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const scrollerRef = React.useRef<HTMLUListElement>(null);
	const [start, setStart] = useState(false);

	/**
	 * Initializes the infinite scroll animation
	 * @effect
	 */
	useEffect(() => {
		if (containerRef.current && scrollerRef.current) {
			// Duplicate items for seamless scrolling
			const scrollerContent = Array.from(scrollerRef.current.children);
			scrollerContent.forEach((item) => {
				const duplicatedItem = item.cloneNode(true);
				if (scrollerRef.current) {
					scrollerRef.current.appendChild(duplicatedItem);
				}
			});

			// Set animation direction
			if (direction === 'left') {
				containerRef.current.style.setProperty('--animation-direction', 'forwards');
			} else {
				containerRef.current.style.setProperty('--animation-direction', 'reverse');
			}

			// Set animation speed
			if (speed === 'fast') {
				containerRef.current.style.setProperty('--animation-duration', '20s');
			} else if (speed === 'normal') {
				containerRef.current.style.setProperty('--animation-duration', '40s');
			} else {
				containerRef.current.style.setProperty('--animation-duration', '80s');
			}

			setStart(true);
		}
	}, [direction, speed]);

	return (
		<div
			ref={containerRef}
			className={cn(
				'scroller relative z-20 max-w-7xl overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]',
				className,
			)}
		>
			<ul
				ref={scrollerRef}
				className={cn(
					'flex w-max min-w-full shrink-0 flex-nowrap gap-4 py-4',
					start && 'animate-scroll',
					pauseOnHover && 'hover:[animation-play-state:paused]',
				)}
			>
				{items.map((item, _idx) => {
					const Icon = item.icon;
					return (
						<li
							className="relative w-[350px] max-w-full shrink-0 rounded-2xl border border-foreground/10 bg-gradient-to-br from-foreground/5 to-transparent px-8 py-8 md:w-[450px] min-h-[300px] flex flex-col backdrop-blur-sm hover:border-primary/20 transition-all duration-300 group"
							key={`${item.title}-${_idx}`}
						>
							<blockquote className="w-full">
								{/* Decorative background */}
								<div
									aria-hidden="true"
									className="user-select-none pointer-events-none absolute -top-0.5 -left-0.5 -z-1 h-[calc(100%_+_4px)] w-[calc(100%_+_4px)]"
								></div>
								{/* Card Header */}
								<div className="relative z-20 mb-6 flex items-center gap-3">
									{Icon && (
										<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
											<Icon className="w-5 h-5 text-red-500" />
										</div>
									)}
									<h3 className="text-sm font-medium text-purple-500 uppercase tracking-wider">
										{item.title}
									</h3>
								</div>
								{/* Quote Content */}
								<span className="relative z-20 text-base leading-[1.8] font-normal text-foreground block mb-6">
									{item.quote}
								</span>
								{/* Card Footer */}
								<div className="relative z-20 mt-auto">
									<span className="flex flex-col gap-1">
										<span className="text-base leading-[1.6] font-semibold text-foreground">
											{item.name}
										</span>
										{item.position && (
											<span className="text-sm text-muted-foreground">{item.position}</span>
										)}
										{item.company && (
											<span className="text-sm text-orange-500/80">{item.company}</span>
										)}
									</span>
								</div>
							</blockquote>
							{showConnectors && (
								<div
									aria-hidden="true"
									className="pointer-events-none absolute top-1/2 -right-6 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full border border-orange-500/40 bg-background/80 p-1.5 shadow-sm backdrop-blur-sm md:flex"
								>
									<ChevronRight className="h-4 w-4 text-orange-500" />
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
};
